import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EndpointFactory } from './endpoint-factory.service';
import { ConfigurationService } from './configuration.service';

import { AbstractSearchKeys } from '../models/abstract/abstract-search.model';
import { Abstract } from '../models/abstract/abstract.model';
import { ActionAbstracts } from '../models/abstract/action-abstracts.model';
import { Roles } from '../models/enums';


@Injectable({ providedIn: 'root'})
export class AbstractEndpoint extends EndpointFactory {

  private readonly _abstractsUrl: string = '/api/abstract';
  private readonly _abstractSubmissionUrl: string = '/api/abstract/submission';
  private readonly _abstractsByParams: string = '/api/abstract/params';
  private readonly _abstractSearchMetadataUrl: string = '/api/abstract/metadata';
  private readonly _abstractCreateUrl: string = '/api/abstract/create';
  private readonly _abstractImportUrl: string = '/api/abstract/import';
  private readonly _abstractSpreadsheetTemplateUrl: string = '/api/abstract/template';
  private readonly _nextAbstractUrl: string = '/api/abstract/next';
  private readonly _actionAbstractsUrl: string = '/api/abstract/actionabstracts';
  private readonly _getOpenAbstractsUrl: string = '/api/abstract/incoding';
  private readonly _getUncodedAbstractsCountUrl: string = '/api/abstract/uncoded/count';
  private readonly _getAbsByParamsReconUrl: string = '/api/abstract/paramsrecon';
  private readonly _autoSampleAbstractsUrl: string = '/api/abstract/autosample';
  private readonly _getAbsByParamsFullResultUrl: string = '/api/abstract/paramsfull';
  private readonly _getProjectsUrl: string = '/api/abstract/getprojbyuser';

  get abstractsUrl() { return this.configurations.baseUrl + this._abstractsUrl; }
  get abstractSubmissionUrl() { return this.configurations.baseUrl + this._abstractSubmissionUrl; }
  get abstractSearchMetadataUrl() { return this.configurations.baseUrl + this._abstractSearchMetadataUrl; }
  get abstractsByParmsUrl() { return this.configurations.baseUrl + this._abstractsByParams; }
  get abstractCreateUrl() { return this.configurations.baseUrl + this._abstractCreateUrl; }
  get abstractImportUrl() { return this.configurations.baseUrl + this._abstractImportUrl; }
  get abstractSpreadsheetTemplateUrl() { return this.configurations.baseUrl + this._abstractSpreadsheetTemplateUrl; }
  get nextAbstractUrl() { return this.configurations.baseUrl + this._nextAbstractUrl; }
  get actionAbstractsUrl() { return this.configurations.baseUrl + this._actionAbstractsUrl; }
  get getOpenAbstractsUrl() { return this.configurations.baseUrl + this._getOpenAbstractsUrl; }
  get uncodedAbstractsCountUrl() { return this.configurations.baseUrl + this._getUncodedAbstractsCountUrl; }
  get getAbsByParamsFullResultUrl() { return this.configurations.baseUrl + this._getAbsByParamsFullResultUrl; }
  get getAbsByParamsReconUrl() { return this.configurations.baseUrl + this._getAbsByParamsReconUrl; }
  get autoSampleAbstractsUrl() { return this.configurations.baseUrl + this._autoSampleAbstractsUrl; } 
  get projectsUrl() { return this.configurations.baseUrl + this._getProjectsUrl; }

  constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
    super(http, configurations, injector);
  }

  getAbstractByIdEndpoint<T>(abstractId: number): Observable<T> {
    const endpointUrl = abstractId ? `${this.abstractsUrl}/${abstractId}` : this.abstractsUrl;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getRoleFilteredAbstractByIdEndpoint<T>(abstractId: number, activeRole: Roles): Observable<T> {
    const endpointUrl = `${this.abstractsUrl}/${abstractId}/rolefiltered/${activeRole}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getNextAbstract<T>(activeRole: Roles, activeTeam: number, activeProject: number, mode: number): Observable<T> {
    const options = {...this.getRequestHeaders()};
    //if (activeRole) { options['params'] = {activeRole}; }

    return this.http.get<T>(this.nextAbstractUrl + `/${activeRole}/${activeTeam}/${activeProject}/${mode}`, options);
  }

  getAbstractSearchMetadataEndpoint<T>(activeRole: Roles, protocolId: number, activeProject: number, filterByProtocol: boolean): Observable<T> {
    return this.http.get<T>(this.abstractSearchMetadataUrl + `/${activeRole}/${protocolId}/${activeProject}/${filterByProtocol}`, this.getRequestHeaders());
  }

  getAbstractsByParamsEndpoint<T>(searchKeys: AbstractSearchKeys): Observable<T> {
    return this.http.post<T>(this.abstractsByParmsUrl, searchKeys, this.getRequestHeaders());
  }

  getAbstractSpreadsheetTemplateEndpoint<T>(): Observable<T> {
    const options = this.getRequestHeaders();
    options['responseType'] = 'arraybuffer';

    return this.http.get<T>(this.abstractSpreadsheetTemplateUrl, options).pipe<T>(
      catchError(error => {
        if (error.error instanceof ArrayBuffer) { error.error = this.DecodeArrayBuffer(error.error); }
        return throwError(error);
      }));
  }

  importAbstractEndpoint<T>(file: File, odpProjectWorkflowId: number, protocolId: number): Observable<T> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('odpProjectWorkflowId', odpProjectWorkflowId.toString());
    formData.append('protocolId', protocolId.toString());

    return this.http.post<T>(this.abstractImportUrl, formData, this.getUploadHeaders());
  }

  addAbstractEndpoint<T>(abstract: Abstract): Observable<T> {
    return this.http.post<T>(this.abstractCreateUrl, JSON.stringify(abstract), this.getRequestHeaders());
  }

  updateAbstractEndpoint<T>(abstract: Abstract): Observable<T> {
    const endpointUrl = `${this.abstractsUrl}/${abstract.id}`;
    return this.http.post<T>(endpointUrl, JSON.stringify(abstract), this.getRequestHeaders());
  }

  updateAbstractStatusEndpoint<T>(abstractList: Abstract[]): Observable<T> {
    return this.http.post<T>(this.abstractsUrl, JSON.stringify(abstractList), this.getRequestHeaders());
  }

  getFullAbstractForEvaluationEndpoint<T>(abstractId: number, protocolId: number, activeRole: Roles, activeTeam: number): Observable<T> {
    const endpointUrl = `${this.abstractsUrl}/${abstractId}/protocol/${protocolId}/role/${activeRole}/${activeTeam}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getEvaluationSubmissionDataEndpoint<T>(submissionId: number, activeRole: Roles): Observable<T> {
    const endpointUrl = `${this.abstractSubmissionUrl}/${submissionId}`;
    const options = { ...this.getRequestHeaders() };
    if (activeRole) { options['params'] = { activeRole }; }

    return this.http.get<T>(endpointUrl, options);
  }

  canUserAttemptCodeAbstract<T>(abstractId: number, protocolId: number, activeRole: Roles, activeTeam: number): Observable<T> {
    const endpointUrl = `${this.abstractsUrl}/${abstractId}/protocol/${protocolId}/canattemptcode/${activeRole}/${activeTeam}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  getOverrideAbstractEndpoint<T>(abstractId: number, protocolId: number, activeRole: Roles): Observable<T> {
    const endpointUrl = `${this.abstractsUrl}/${abstractId}/protocol/${protocolId}/override/${activeRole}`;
    return this.http.get<T>(endpointUrl, this.getRequestHeaders());
  }

  changeAbstractsStatusEndpoint<T>(actionAbstracts: ActionAbstracts, odpProjectWorkflowId: number): Observable<T> {
    const formData = new FormData();
    formData.append('actionAbstracts', JSON.stringify(actionAbstracts));
    formData.append('odpProjectWorkflowId', odpProjectWorkflowId.toString());
    return this.http.post<T>(this.actionAbstractsUrl, formData, this.getUploadHeaders());

    //return this.http.post<T>(this.actionAbstractsUrl, JSON.stringify(actionAbstracts), this.getRequestHeaders());
  }

  getOpenAbstractEndpoint<T>(activeRole: Roles, activeTeam: number, abstractId: number): Observable<T> {
    return this.http.get<T>(this.getOpenAbstractsUrl + `/${activeRole}/${activeTeam}/${abstractId}`, this.getRequestHeaders());
  }

  getUncodedAbstractsCountEndpoint<T>(activeRole: Roles, activeTeam: number): Observable<T> {
    return this.http.get<T>(this.uncodedAbstractsCountUrl + `/${activeRole}/${activeTeam}`, this.getRequestHeaders());
  }


  getAbsByParamsReconEndpoint<T>(searchKeys: AbstractSearchKeys): Observable<T> {
    return this.http.post<T>(this.getAbsByParamsReconUrl, searchKeys, this.getRequestHeaders());
  }

  getAutoSampleAbstractsEndpoint<T>(searchKeys: AbstractSearchKeys): Observable<T> {
    return this.http.post<T>(this.autoSampleAbstractsUrl, searchKeys, this.getRequestHeaders());
  }

  getAbsByParamsFullResultEndpoint<T>(searchKeys: AbstractSearchKeys): Observable<T> {
    return this.http.post<T>(this.getAbsByParamsFullResultUrl, searchKeys, this.getRequestHeaders());
  }


  getUserProjectsEndpoint<T>(userId: string, roleId: string): Observable<T> {
    return this.http.get<T>(this.projectsUrl + `/${userId}/${roleId}`, this.getRequestHeaders());
  }

}
